import { get, post, patch } from './https';

export type PartnerStatus =
  | 'DRAFT'
  | 'UNDER_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'SUSPENDED';

export type PartnerType =
  | 'INSTALLER'
  | 'PLUMBER'
  | 'ELECTRICIAN'
  | 'ARCHITECT'
  | 'ESTATE_AGENT'
  | 'PROPERTY_DEVELOPER'
  | 'ENERGY_ASSESSOR'
  | 'MERCHANT'
  | string;

export type BusinessType = 'LIMITED_COMPANY' | 'SOLE_TRADER';

export type DocType =
  | 'AADHAAR'
  | 'PAN'
  | 'VOTER_ID'
  | 'DRIVING_LICENCE'
  | 'PASSPORT';

export const PARTNER_STATUS_OPTIONS: PartnerStatus[] = [
  'DRAFT',
  'UNDER_REVIEW',
  'APPROVED',
  'REJECTED',
  'SUSPENDED',
];

export const PARTNER_TYPE_OPTIONS: PartnerType[] = [
  'INSTALLER',
  'PLUMBER',
  'ELECTRICIAN',
  'ARCHITECT',
  'ESTATE_AGENT',
  'PROPERTY_DEVELOPER',
  'ENERGY_ASSESSOR',
  'MERCHANT',
];

export interface PartnerSummary {
  id: string;
  fullName: string | null;
  phoneNumber: string;
  email: string | null;
  partnerType: string | null;
  status: PartnerStatus;
  derivedStatus: PartnerStatus;
  onboardingStep: string;
  submitted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PartnerStats {
  total: number;
  byStatus: Record<PartnerStatus, number>;
}

export interface PartnerDetailState {
  partner: {
    id: string;
    phoneNumber: string;
    email: string | null;
    fullName: string | null;
    dateOfBirth: string | null;
    partnerType: string | null;
    profilePhoto: string;
    status: PartnerStatus;
    onboardingStep: string;
    submitted: boolean;
    reviewedBy: string | null;
    reviewedAt: string | null;
  };
  firm: {
    id: string;
    businessType: BusinessType;
    legalName: string;
    tradingName?: string;
    cin?: string;
    gstin?: string;
  } | null;
  address: {
    pincode: string;
    line1: string;
    line2?: string;
    city: string;
    state: string;
  } | null;
  bank: {
    accountHolderName: string;
    ifscCode: string;
    copResult: string;
    copCheckedAt: string | null;
    isActive: boolean;
  } | null;
  identityDocument: { id: string; docType: DocType; uploaded: boolean } | null;
  checks: {
    checkType: string;
    result: string;
    failureReason: string | null;
    reviewedBy: string | null;
    checkedAt: string | null;
  }[];
  requiredChecks: string[];
  agreements: {
    agreementType: string;
    agreementVersion: string;
    acceptedAt: string;
  }[];
  derivedStatus: PartnerStatus;
}

interface ListResponse {
  status: boolean;
  data: PartnerSummary[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

const BASE = '/api/v1/admin/partner-onboarding';

export const getChannelPartnerStats = async (): Promise<PartnerStats> => {
  const response = await get(`${BASE}/stats`);
  if (response.status === 200 && response.data?.status) {
    return response.data.data;
  }
  throw new Error(response?.data?.message || 'Failed to fetch partner stats');
};

export const getChannelPartners = async ({
  page = 1,
  limit = 25,
  search = '',
  status,
  partnerType,
}: {
  page?: number;
  limit?: number;
  search?: string;
  status?: PartnerStatus | '';
  partnerType?: string;
}): Promise<ListResponse> => {
  let url = `${BASE}/partners?page=${page}&limit=${limit}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  if (status) url += `&status=${status}`;
  if (partnerType) url += `&partnerType=${encodeURIComponent(partnerType)}`;

  const response = await get(url);
  if (response.status === 200 && response.data?.status) {
    return response.data;
  }
  throw new Error(response?.data?.message || 'Failed to fetch channel partners');
};

export const getChannelPartnerState = async (
  id: string,
): Promise<PartnerDetailState> => {
  const response = await get(`${BASE}/partners/${id}`);
  if (response.status === 200 && response.data?.status) {
    return response.data.data;
  }
  throw new Error(response?.data?.message || 'Failed to fetch partner details');
};

export interface CreateChannelPartnerPayload {
  fullName: string;
  phoneNumber: string;
  email: string;
  dateOfBirth: string;
  partnerType?: string;
  profilePhoto?: string;
  business?: {
    businessType: BusinessType;
    legalName: string;
    tradingName?: string;
    cin?: string;
    pan?: string;
    gstin?: string;
  };
  address?: {
    pincode: string;
    line1: string;
    line2?: string;
    city: string;
    state: string;
  };
  bank?: {
    accountHolderName: string;
    ifscCode: string;
    accountNumber: string;
  };
}

export const createChannelPartner = async (
  payload: CreateChannelPartnerPayload,
): Promise<{ id: string }> => {
  const response = await post(`${BASE}/partners`, payload);
  if (response.status === 201 && response.data?.status) {
    return response.data.data;
  }
  throw new Error(response?.data?.message || 'Failed to create channel partner');
};

export const suspendChannelPartner = async (id: string) => {
  const response = await post(`${BASE}/partners/${id}/suspend`, {});
  if (response.status === 200 && response.data?.status) {
    return response.data;
  }
  throw new Error(response?.data?.message || 'Failed to suspend partner');
};

export const unsuspendChannelPartner = async (id: string) => {
  const response = await post(`${BASE}/partners/${id}/unsuspend`, {});
  if (response.status === 200 && response.data?.status) {
    return response.data;
  }
  throw new Error(response?.data?.message || 'Failed to unsuspend partner');
};

export const updatePartnerProfile = async (
  id: string,
  payload: {
    fullName?: string;
    email?: string;
    dateOfBirth?: string;
    partnerType?: string;
    profilePhoto?: string;
  },
) => {
  const data = await patch(`${BASE}/partners/${id}/profile`, payload);
  if (data?.status) return data;
  throw new Error(data?.message || 'Failed to update profile');
};

export const updatePartnerBusiness = async (
  id: string,
  payload: {
    businessType: BusinessType;
    legalName: string;
    tradingName?: string;
    cin?: string;
    pan?: string;
    gstin?: string;
  },
) => {
  const data = await patch(`${BASE}/partners/${id}/business`, payload);
  if (data?.status) return data;
  throw new Error(data?.message || 'Failed to update business details');
};

export const updatePartnerAddress = async (
  id: string,
  payload: {
    pincode: string;
    line1: string;
    line2?: string;
    city: string;
    state: string;
  },
) => {
  const data = await patch(`${BASE}/partners/${id}/address`, payload);
  if (data?.status) return data;
  throw new Error(data?.message || 'Failed to update address');
};

export const updatePartnerBank = async (
  id: string,
  payload: {
    accountHolderName: string;
    ifscCode: string;
    accountNumber: string;
  },
) => {
  const data = await patch(`${BASE}/partners/${id}/bank`, payload);
  if (data?.status) return data;
  throw new Error(data?.message || 'Failed to update bank details');
};

export interface IdentityQueueEntry {
  checkId: string;
  partnerId: string;
  fullName: string | null;
  phoneNumber: string;
  partnerType: string | null;
  docId: string | null;
  docType: DocType | null;
  uploadedAt: string | null;
}

export const getIdentityQueue = async (): Promise<IdentityQueueEntry[]> => {
  const response = await get(`${BASE}/identity/queue`);
  if (response.status === 200 && response.data?.status) {
    return response.data.data;
  }
  throw new Error(response?.data?.message || 'Failed to fetch identity queue');
};

export const getIdentityDocumentViewUrl = async (
  docId: string,
): Promise<{ signedUrl: string; contentType: string }> => {
  const response = await get(`${BASE}/identity/${docId}/view`);
  if (response.status === 200 && response.data?.status) {
    return response.data.data;
  }
  throw new Error(response?.data?.message || 'Failed to load document');
};

export const decideIdentityCheck = async (
  checkId: string,
  payload: { result: 'PASSED' | 'FAILED'; failureReason?: string },
) => {
  const response = await post(`${BASE}/identity/${checkId}/decision`, payload);
  if (response.status === 200 && response.data?.status) {
    return response.data;
  }
  throw new Error(response?.data?.message || 'Failed to record decision');
};
